<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Modal from 'dashboard/components/Modal.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  show: Boolean,
  message: { type: Object, required: true },
});

const emit = defineEmits(['close', 'update:show']);

const showModal = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

const store = useStore();
const { t } = useI18n();
const text = ref('');

const strippedContent = msg => {
  const content = (msg?.content || '').toString();
  // If message was previously edited, strip the header line: "✍️ Message edited\n"
  if (content.startsWith('✍️')) {
    const idx = content.indexOf('\n\n');
    return idx >= 0 ? content.slice(idx + 1) : content;
  }
  return content;
};

watch(
  () => props.message,
  m => {
    text.value = strippedContent(m);
  },
  { immediate: true }
);

function onClose() {
  emit('update:show', false);
  emit('close');
}

async function onSubmit() {
  try {
    await store.dispatch('editMessage', {
      conversationId: props.message.conversation_id,
      messageId: props.message.id,
      content: text.value,
    });
    useAlert(t('EDIT_MESSAGE_MODAL.SUCCESS'));
    onClose();
  } catch (e) {
    useAlert(t('EDIT_MESSAGE_MODAL.ERROR'));
  }
}
</script>

<template>
  <Modal v-model:show="showModal" :on-close="onClose">
    <woot-modal-header
      :header-title="t('EDIT_MESSAGE_MODAL.TITLE')"
      :header-content="t('EDIT_MESSAGE_MODAL.DESC')"
    />
    <div class="p-8 pt-4">
      <label class="block text-xs font-medium mb-1">
        {{ t('EDIT_MESSAGE_MODAL.LABEL') }}
      </label>
      <textarea
        v-model="text"
        rows="5"
        class="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm"
        :placeholder="t('EDIT_MESSAGE_MODAL.PLACEHOLDER')"
      />
    </div>
    <div class="flex items-center justify-end gap-2 p-8 pt-4">
      <Button faded slate :label="t('GENERAL.CLOSE')" @click="onClose" />
      <Button :label="t('EDIT_MESSAGE_MODAL.SUBMIT')" @click="onSubmit" />
    </div>
  </Modal>
</template>
